/**
 * Odd World Studio — Find Your Camera
 * Adaptive quiz engine + results rendering.
 *
 * This file is intentionally framework-free (vanilla JS) so it can be dropped
 * into a Shopify theme as a plain asset file with zero build step.
 *
 * Config (all optional — sensible defaults below):
 *   window.OWS_CAMERA_FINDER_CONFIG = {
 *     apiBase: "https://your-backend.up.railway.app",
 *     amazonTag: "johnnyjoo-20"
 *   }
 */
(function () {
  "use strict";

  var CONFIG = Object.assign(
    {
      apiBase: "https://camera-finder-api-production.up.railway.app",
      amazonTag: "johnnyjoo-20",
    },
    window.OWS_CAMERA_FINDER_CONFIG || {}
  );

  // ---------------------------------------------------------------------
  // 1. QUESTION BANK
  // ---------------------------------------------------------------------
  // Each question is shown in this order UNLESS showIf(answers) returns
  // false, in which case it's skipped. This gives us adaptive branching
  // without a full graph: later questions react to earlier answers.
  var QUESTIONS = [
    {
      id: "experience",
      title: "Where are you starting from?",
      subtitle: "There's no wrong answer here — this just shapes how we explain things.",
      type: "single",
      options: [
        { value: "brand_new", label: "I've never really used a “real” camera before", hint: "Just my phone, mostly" },
        { value: "some_experience", label: "I shoot semi-regularly and want to upgrade", hint: "I know my way around, but I'm ready for more" },
        { value: "returning", label: "I used to shoot and want back in", hint: "Picking the hobby back up" },
        { value: "know_exactly", label: "I basically know what I want", hint: "Just help me land on a specific model" },
      ],
    },
    {
      id: "primary_use",
      title: "What do you mainly want to shoot?",
      subtitle: "Pick up to 3 — we'll tailor a few questions based on this.",
      type: "multi",
      maxSelect: 3,
      options: [
        { value: "portraits", label: "Portraits / people" },
        { value: "street", label: "Street & everyday life" },
        { value: "travel", label: "Travel" },
        { value: "landscape", label: "Landscape & nature" },
        { value: "action", label: "Sports, action & wildlife" },
        { value: "video", label: "Video / hybrid content" },
        { value: "lowlight", label: "Concerts & low-light events" },
        { value: "film_look", label: "That film look, mostly for fun" },
        { value: "pro", label: "Paid client work" },
      ],
    },
    {
      id: "format",
      title: "Film, digital, or open to either?",
      subtitle: "Both have a real place — we sell and service both here.",
      type: "single",
      options: [
        { value: "digital", label: "Digital" },
        { value: "film", label: "Film" },
        { value: "either", label: "Honestly, open to either" },
        { value: "instant", label: "Something fun & instant" },
      ],
    },

    // ---- Film branch ----
    {
      id: "film_format",
      title: "What kind of film shooting sounds right?",
      type: "single",
      showIf: function (a) { return a.format === "film" || a.format === "either"; },
      options: [
        { value: "35mm_point_shoot", label: "35mm point-and-shoot", hint: "Simple, compact, grab-and-go" },
        { value: "35mm_manual", label: "35mm SLR with manual control", hint: "I want to learn the dials" },
        { value: "medium_format", label: "Medium format", hint: "Bigger negatives, deliberate shooting" },
        { value: "not_sure", label: "Not sure yet — show me options" },
      ],
    },
    {
      id: "film_involvement",
      title: "How hands-on do you want developing to be?",
      type: "single",
      showIf: function (a) { return a.format === "film" || a.format === "either"; },
      options: [
        { value: "mail_in", label: "I'll mail it in / drop it off", hint: "We do this — just want the camera right" },
        { value: "home_dev", label: "I want to develop at home eventually" },
        { value: "doesnt_matter", label: "Doesn't affect the camera choice for me" },
      ],
    },

    // ---- Digital branch ----
    {
      id: "system",
      title: "Any brand loyalty, or gear you already own?",
      subtitle: "If you already own lenses, staying in-system saves you money.",
      type: "single",
      showIf: function (a) { return a.format === "digital" || a.format === "either"; },
      options: [
        { value: "canon", label: "Canon" },
        { value: "nikon", label: "Nikon" },
        { value: "sony", label: "Sony" },
        { value: "fujifilm", label: "Fujifilm" },
        { value: "m43", label: "Panasonic / Micro Four Thirds" },
        { value: "none", label: "No preference — open to anything" },
      ],
    },
    {
      id: "viewfinder",
      title: "How do you like to compose a shot?",
      type: "single",
      showIf: function (a) { return a.format === "digital" || a.format === "either"; },
      options: [
        { value: "viewfinder", label: "I want a real viewfinder up to my eye" },
        { value: "screen_ok", label: "The screen is fine — I want it small & simple" },
        { value: "no_preference", label: "No preference" },
      ],
    },

    // ---- Use-case-specific follow-ups ----
    {
      id: "action_priority",
      title: "How much do fast autofocus and burst speed matter?",
      subtitle: "You picked sports / action / wildlife — this is usually the deciding factor there.",
      type: "single",
      showIf: function (a) { return (a.primary_use || []).indexOf("action") !== -1; },
      options: [
        { value: "critical", label: "Critical — I can't miss the moment" },
        { value: "nice_to_have", label: "Nice to have, not a dealbreaker" },
        { value: "not_really", label: "Not really my main concern" },
      ],
    },
    {
      id: "video_priority",
      title: "What matters more for video?",
      subtitle: "You mentioned video / hybrid content.",
      type: "single",
      showIf: function (a) { return (a.primary_use || []).indexOf("video") !== -1; },
      options: [
        { value: "casual", label: "Just good casual video" },
        { value: "serious", label: "Real specs — 4K, log profiles, the works" },
        { value: "not_priority", label: "Video's secondary, honestly" },
      ],
    },
    {
      id: "lowlight_priority",
      title: "How much do low light performance and background blur matter?",
      subtitle: "You mentioned portraits or low-light events.",
      type: "single",
      showIf: function (a) {
        var u = a.primary_use || [];
        return u.indexOf("portraits") !== -1 || u.indexOf("lowlight") !== -1;
      },
      options: [
        { value: "critical", label: "Very important — dim rooms, blurry backgrounds" },
        { value: "somewhat", label: "Somewhat — nice when I get it" },
        { value: "not_really", label: "Not a big factor" },
      ],
    },
    {
      id: "weather_sealing",
      title: "Will this see rough weather?",
      subtitle: "You mentioned landscape / nature.",
      type: "single",
      showIf: function (a) { return (a.primary_use || []).indexOf("landscape") !== -1; },
      options: [
        { value: "yes", label: "Yes — rain, dust, cold, all of it" },
        { value: "sometimes", label: "Sometimes, nothing extreme" },
        { value: "no", label: "Mostly fair-weather shooting" },
      ],
    },
    {
      id: "pro_use",
      title: "Is this a primary work camera, or a backup body?",
      subtitle: "You mentioned paid client work.",
      type: "single",
      showIf: function (a) { return (a.primary_use || []).indexOf("pro") !== -1; },
      options: [
        { value: "primary", label: "This needs to be my main camera" },
        { value: "backup", label: "It's a second body / backup" },
      ],
    },

    // ---- Universal wrap-up ----
    {
      id: "budget",
      title: "What's the budget for the camera body?",
      subtitle: "Lenses and accessories are separate — this is just the body (or the whole camera, for film point-and-shoots).",
      type: "single",
      options: [
        { value: "under_300", label: "Under $300" },
        { value: "300_700", label: "$300 – $700" },
        { value: "700_1500", label: "$700 – $1,500" },
        { value: "1500_3000", label: "$1,500 – $3,000" },
        { value: "3000_plus", label: "$3,000+" },
        { value: "not_sure", label: "Not sure — show me a spread" },
      ],
    },
    {
      id: "size_weight",
      title: "How much does size and weight matter?",
      type: "single",
      options: [
        { value: "small", label: "I want something small I'll actually carry" },
        { value: "doesnt_matter", label: "Doesn't matter much to me" },
        { value: "capable", label: "I don't mind bigger if it's more capable" },
      ],
    },
    {
      id: "notes",
      title: "Anything else we should know?",
      subtitle: "A trip you're prepping for, a look you love, a camera you've been eyeing — anything.",
      type: "text",
      optional: true,
    },
  ];

  // ---------------------------------------------------------------------
  // 2. ENGINE
  // ---------------------------------------------------------------------
  function CameraFinder(root) {
    this.root = root;
    this.answers = {};
    this.path = []; // ordered list of question ids actually shown
    this.started = false;
    this.render();
  }

  CameraFinder.prototype.visibleQuestions = function () {
    var self = this;
    return QUESTIONS.filter(function (q) {
      return !q.showIf || q.showIf(self.answers);
    });
  };

  CameraFinder.prototype.nextQuestion = function () {
    var visible = this.visibleQuestions();
    for (var i = 0; i < visible.length; i++) {
      if (!(visible[i].id in this.answers)) return visible[i];
    }
    return null;
  };

  CameraFinder.prototype.currentProgress = function () {
    var visible = this.visibleQuestions();
    var answeredCount = this.path.length;
    return { step: answeredCount + 1, total: Math.max(visible.length, answeredCount + 1) };
  };

  CameraFinder.prototype.answer = function (id, value) {
    this.answers[id] = value;
    if (this.path.indexOf(id) === -1) this.path.push(id);
    this.render();
  };

  CameraFinder.prototype.goBack = function () {
    if (!this.path.length) return;
    var lastId = this.path.pop();
    delete this.answers[lastId];
    this.render();
  };

  CameraFinder.prototype.start = function () {
    this.started = true;
    this.render();
  };

  CameraFinder.prototype.restart = function () {
    this.answers = {};
    this.path = [];
    this.started = false;
    this.render();
  };

  CameraFinder.prototype.render = function () {
    if (!this.started) return this.renderIntro();
    var q = this.nextQuestion();
    if (q) return this.renderQuestion(q);
    return this.renderLoadingThenResults();
  };

  // ---- Intro screen ----
  CameraFinder.prototype.renderIntro = function () {
    var self = this;
    this.root.innerHTML =
      '<div class="ows-cf-intro">' +
      '<div class="ows-cf-eyebrow">ODD WORLD STUDIO &middot; CAMERA FINDER</div>' +
      '<h1 class="ows-cf-title">FIND YOUR<br><span class="ows-cf-accent">CAMERA</span></h1>' +
      "<p class=\"ows-cf-lede\">Answer a few questions &mdash; we'll ask more or less depending on what you tell us &mdash; and we'll match you with a camera that actually fits how you shoot, plus a couple of worthy runners-up.</p>" +
      '<div class="ows-cf-meta">Time: ~2&ndash;3 minutes</div>' +
      '<button type="button" class="ows-cf-btn ows-cf-btn--primary" data-action="start">START THE QUIZ &rarr;</button>' +
      "</div>";
    this.root.querySelector('[data-action="start"]').addEventListener("click", function () {
      self.start();
    });
  };

  // ---- Question screen ----
  CameraFinder.prototype.renderQuestion = function (q) {
    var self = this;
    var progress = this.currentProgress();
    var pct = Math.min(100, Math.round((progress.step - 1) / progress.total * 100));

    var html = '<div class="ows-cf-quiz">';
    html += '<div class="ows-cf-progress"><div class="ows-cf-progress-bar" style="width:' + pct + '%"></div></div>';
    html += '<div class="ows-cf-step">Question ' + progress.step + ' of about ' + progress.total + "</div>";
    html += '<h2 class="ows-cf-q-title">' + escapeHtml(q.title) + "</h2>";
    if (q.subtitle) html += '<p class="ows-cf-q-subtitle">' + escapeHtml(q.subtitle) + "</p>";

    if (q.type === "single") {
      html += '<div class="ows-cf-options">';
      q.options.forEach(function (opt) {
        html +=
          '<button type="button" class="ows-cf-option" data-value="' + escapeAttr(opt.value) + '">' +
          '<span class="ows-cf-option-label">' + escapeHtml(opt.label) + "</span>" +
          (opt.hint ? '<span class="ows-cf-option-hint">' + escapeHtml(opt.hint) + "</span>" : "") +
          "</button>";
      });
      html += "</div>";
    } else if (q.type === "multi") {
      html += '<div class="ows-cf-options ows-cf-options--multi" data-max="' + (q.maxSelect || 99) + '">';
      q.options.forEach(function (opt) {
        html +=
          '<button type="button" class="ows-cf-option ows-cf-option--multi" data-value="' + escapeAttr(opt.value) + '">' +
          '<span class="ows-cf-option-label">' + escapeHtml(opt.label) + "</span>" +
          "</button>";
      });
      html += "</div>";
      html += '<button type="button" class="ows-cf-btn ows-cf-btn--primary ows-cf-btn--continue" data-action="continue-multi" disabled>CONTINUE &rarr;</button>';
    } else if (q.type === "text") {
      html += '<textarea class="ows-cf-textarea" rows="3" placeholder="Optional — write as much or as little as you want"></textarea>';
      html +=
        '<div class="ows-cf-row">' +
        (q.optional ? '<button type="button" class="ows-cf-btn ows-cf-btn--ghost" data-action="skip-text">SKIP</button>' : "") +
        '<button type="button" class="ows-cf-btn ows-cf-btn--primary" data-action="submit-text">CONTINUE &rarr;</button>' +
        "</div>";
    }

    html += this.path.length
      ? '<button type="button" class="ows-cf-back" data-action="back">&larr; Back</button>'
      : "";
    html += "</div>";

    this.root.innerHTML = html;

    if (q.type === "single") {
      this.root.querySelectorAll(".ows-cf-option").forEach(function (btn) {
        btn.addEventListener("click", function () {
          self.answer(q.id, btn.getAttribute("data-value"));
        });
      });
    } else if (q.type === "multi") {
      var selected = [];
      var maxSelect = q.maxSelect || 99;
      var continueBtn = this.root.querySelector('[data-action="continue-multi"]');
      this.root.querySelectorAll(".ows-cf-option--multi").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var val = btn.getAttribute("data-value");
          var idx = selected.indexOf(val);
          if (idx !== -1) {
            selected.splice(idx, 1);
            btn.classList.remove("is-selected");
          } else {
            if (selected.length >= maxSelect) return;
            selected.push(val);
            btn.classList.add("is-selected");
          }
          continueBtn.disabled = selected.length === 0;
        });
      });
      continueBtn.addEventListener("click", function () {
        self.answer(q.id, selected.slice());
      });
    } else if (q.type === "text") {
      var textarea = this.root.querySelector(".ows-cf-textarea");
      var submit = this.root.querySelector('[data-action="submit-text"]');
      var skip = this.root.querySelector('[data-action="skip-text"]');
      submit.addEventListener("click", function () {
        self.answer(q.id, textarea.value.trim());
      });
      if (skip) skip.addEventListener("click", function () { self.answer(q.id, ""); });
    }

    var back = this.root.querySelector('[data-action="back"]');
    if (back) back.addEventListener("click", function () { self.goBack(); });
  };

  // ---- Loading + results ----
  CameraFinder.prototype.renderLoadingThenResults = function () {
    var self = this;
    this.root.innerHTML =
      '<div class="ows-cf-loading">' +
      '<div class="ows-cf-spinner"></div>' +
      '<p>Matching you with a camera&hellip;</p>' +
      "</div>";

    fetchRecommendation(this.answers)
      .then(function (data) {
        self.renderResults(data, false);
      })
      .catch(function (err) {
        console.error("Camera Finder: recommendation request failed, using fallback.", err);
        self.renderResults(fallbackRecommendation(self.answers), true);
      });
  };

  CameraFinder.prototype.renderResults = function (data, isFallback) {
    var self = this;
    var html = '<div class="ows-cf-results">';
    html += '<div class="ows-cf-eyebrow">YOUR MATCH</div>';
    if (data.summary) html += '<p class="ows-cf-summary">' + escapeHtml(data.summary) + "</p>";
    if (isFallback) {
      html +=
        '<p class="ows-cf-notice">We couldn’t reach our recommendation engine just now, so here’s a solid pick based on your answers directly — refresh to try again for a fully tailored match.</p>';
    }

    html += renderCard(data.primary, true);

    if (data.runnersUp && data.runnersUp.length) {
      html += '<h3 class="ows-cf-runners-title">Worth a look, too</h3>';
      html += '<div class="ows-cf-runners">';
      data.runnersUp.forEach(function (item) {
        html += renderCard(item, false);
      });
      html += "</div>";
    }

    html += '<button type="button" class="ows-cf-btn ows-cf-btn--ghost ows-cf-restart" data-action="restart">START OVER</button>';
    html += "</div>";

    this.root.innerHTML = html;
    this.root.querySelector('[data-action="restart"]').addEventListener("click", function () {
      self.restart();
    });
  };

  function renderCard(item, isPrimary) {
    if (!item) return "";
    var html = '<div class="ows-cf-card ' + (isPrimary ? "ows-cf-card--primary" : "") + '">';
    html += '<div class="ows-cf-card-name">' + escapeHtml(item.name || "") + "</div>";
    if (item.priceRange) html += '<div class="ows-cf-card-price">' + escapeHtml(item.priceRange) + "</div>";
    if (item.why) html += '<p class="ows-cf-card-why">' + escapeHtml(item.why) + "</p>";
    html += '<div class="ows-cf-card-links">';
    if (item.shopUrl) {
      html += '<a class="ows-cf-btn ows-cf-btn--primary ows-cf-btn--small" href="' + escapeAttr(item.shopUrl) + '">VIEW IN OUR SHOP &rarr;</a>';
    }
    if (item.amazonSearchUrl) {
      html += '<a class="ows-cf-btn ows-cf-btn--outline ows-cf-btn--small" href="' + escapeAttr(item.amazonSearchUrl) + '" target="_blank" rel="noopener sponsored">SEE ON AMAZON &rarr;</a>';
    }
    html += "</div></div>";
    return html;
  }

  // ---------------------------------------------------------------------
  // 3. API + FALLBACK
  // ---------------------------------------------------------------------
  function fetchRecommendation(answers) {
    var controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    var timeout = setTimeout(function () { if (controller) controller.abort(); }, 75000);

    return fetch(CONFIG.apiBase.replace(/\/$/, "") + "/api/recommend", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ answers: answers, amazonTag: CONFIG.amazonTag }),
      signal: controller ? controller.signal : undefined,
    })
      .then(function (res) {
        clearTimeout(timeout);
        if (!res.ok) throw new Error("Bad response: " + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.primary) throw new Error("Malformed response");
        return data;
      });
  }

  // A simple, honest, non-AI fallback so the tool never dead-ends if the
  // backend is briefly unreachable. Deliberately modest in scope.
  function fallbackRecommendation(a) {
    var amazonTag = CONFIG.amazonTag;
    function amazonSearch(q) {
      return "https://www.amazon.com/s?k=" + encodeURIComponent(q) + "&tag=" + encodeURIComponent(amazonTag);
    }

    if (a.format === "film") {
      if (a.film_format === "35mm_point_shoot" || a.experience === "brand_new") {
        return {
          summary: "Based on your answers, an easy, reliable 35mm point-and-shoot is the least friction way to start shooting film.",
          primary: {
            name: "A reliable 35mm point-and-shoot film camera",
            priceRange: "Typically $80–250 depending on condition",
            why: "Fully automatic exposure and focus, so you can focus on composing instead of settings — a great low-stakes way to learn film.",
            amazonSearchUrl: amazonSearch("35mm point and shoot film camera"),
          },
          runnersUp: [],
        };
      }
      return {
        summary: "You're leaning toward manual film — a classic mechanical 35mm SLR gives you full control and is easy to get serviced.",
        primary: {
          name: "A manual 35mm SLR (e.g. Pentax K1000-style body)",
          priceRange: "Typically $100–300 body-only",
          why: "Fully manual exposure teaches the fundamentals fast, and parts/repairs are widely available.",
          amazonSearchUrl: amazonSearch("manual 35mm SLR film camera"),
        },
        runnersUp: [],
      };
    }

    if (a.experience === "brand_new") {
      return {
        summary: "For a first real camera, an entry-level mirrorless kit balances simplicity now with room to grow.",
        primary: {
          name: "An entry-level mirrorless camera with kit lens",
          priceRange: "Typically $500–900 with lens",
          why: "Easy auto modes for day one, full manual controls once you're ready, and an upgrade path via lenses.",
          amazonSearchUrl: amazonSearch("beginner mirrorless camera kit"),
        },
        runnersUp: [],
      };
    }

    return {
      summary: "Based on what you shared, a mid-range mirrorless body fits most of what you're after.",
      primary: {
        name: "A mid-range mirrorless camera body",
        priceRange: "Varies with your budget range",
        why: "A solid general-purpose match for your stated priorities — refresh in a moment for a fully tailored pick.",
        amazonSearchUrl: amazonSearch("mirrorless camera"),
      },
      runnersUp: [],
    };
  }

  // ---------------------------------------------------------------------
  // 4. Helpers
  // ---------------------------------------------------------------------
  function escapeHtml(str) {
    return String(str == null ? "" : str).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function escapeAttr(str) {
    return escapeHtml(str);
  }

  // ---------------------------------------------------------------------
  // 5. Mount
  // ---------------------------------------------------------------------
  function mountAll() {
    var roots = document.querySelectorAll("[data-ows-camera-finder]");
    roots.forEach(function (root) {
      new CameraFinder(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mountAll);
  } else {
    mountAll();
  }

  // Expose for debugging / Shopify theme editor re-mount on section reload
  window.OWSCameraFinder = { mountAll: mountAll, CameraFinder: CameraFinder };
})();
